/**
 * useBiometricAuth - React hook for biometric authentication
 * Provides state management and actions for WebAuthn biometric login
 */

import { useState, useEffect, useCallback } from 'react';
import {
  isWebAuthnSupported,
  isPlatformAuthenticatorAvailable,
  hasEnrolledCredential,
  registerBiometricCredential,
  authenticateWithBiometric,
  removeBiometricCredential,
  getBiometricTypeDescription,
  BiometricRegistrationOptions,
  BiometricRegistrationResult,
  BiometricAuthResult,
} from '../utils/biometricAuth';

/**
 * Biometric authentication state
 */
export interface BiometricState {
  // Support status
  isSupported: boolean;
  isPlatformAvailable: boolean;
  isEnrolled: boolean;
  canUseBiometrics: boolean;

  // Loading states
  isLoading: boolean;
  isRegistering: boolean;
  isAuthenticating: boolean;

  // Error state
  error: string | null;

  // User-friendly biometric type description
  biometricType: string;
}

/**
 * Biometric authentication actions
 */
export interface BiometricActions {
  /**
   * Check current biometric support status
   */
  checkStatus: () => Promise<void>;

  /**
   * Register/enroll biometric credential
   */
  register: (options: Omit<BiometricRegistrationOptions, 'rpId' | 'rpName'>) => Promise<BiometricRegistrationResult>;

  /**
   * Authenticate using biometrics
   */
  authenticate: () => Promise<BiometricAuthResult>;

  /**
   * Remove enrolled credential
   */
  unenroll: () => void;

  /**
   * Clear error state
   */
  clearError: () => void;
}

/**
 * Hook return type
 */
export interface UseBiometricAuthReturn extends BiometricState, BiometricActions {}

/**
 * React hook for biometric authentication
 * Manages state and provides methods for WebAuthn operations
 */
export const useBiometricAuth = (): UseBiometricAuthReturn => {
  // State
  const [isSupported, setIsSupported] = useState(false);
  const [isPlatformAvailable, setIsPlatformAvailable] = useState(false);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isRegistering, setIsRegistering] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [biometricType, setBiometricType] = useState('Biometric Authentication');

  /**
   * Check current biometric support status
   */
  const checkStatus = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Check WebAuthn support
      const supported = isWebAuthnSupported();
      setIsSupported(supported);

      if (supported) {
        // Check platform authenticator
        const platformAvailable = await isPlatformAuthenticatorAvailable();
        setIsPlatformAvailable(platformAvailable);

        // Check if already enrolled
        const enrolled = hasEnrolledCredential();
        setIsEnrolled(enrolled);

        // Get biometric type description
        const type = getBiometricTypeDescription();
        setBiometricType(type);
      } else {
        setIsPlatformAvailable(false);
        setIsEnrolled(false);
      }
    } catch (err) {
      console.error('Error checking biometric status:', err);
      setError('Failed to check biometric support');
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Register/enroll biometric credential
   */
  const register = useCallback(
    async (
      options: Omit<BiometricRegistrationOptions, 'rpId' | 'rpName'>
    ): Promise<BiometricRegistrationResult> => {
      setIsRegistering(true);
      setError(null);

      try {
        const result = await registerBiometricCredential({
          ...options,
          rpName: 'ChecksAndTicks',
        });

        if (result.success) {
          setIsEnrolled(true);
        } else if (result.error) {
          setError(result.error);
        }

        return result;
      } catch (err) {
        const errorMessage = 'Failed to register biometric credential';
        setError(errorMessage);
        return { success: false, error: errorMessage };
      } finally {
        setIsRegistering(false);
      }
    },
    []
  );

  /**
   * Authenticate using biometrics
   */
  const authenticate = useCallback(async (): Promise<BiometricAuthResult> => {
    setIsAuthenticating(true);
    setError(null);

    try {
      const result = await authenticateWithBiometric();

      if (!result.success && result.error) {
        setError(result.error);
      }

      return result;
    } catch (err) {
      const errorMessage = 'Biometric authentication failed';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsAuthenticating(false);
    }
  }, []);

  /**
   * Remove enrolled credential
   */
  const unenroll = useCallback(() => {
    removeBiometricCredential();
    setIsEnrolled(false);
    setError(null);
  }, []);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Check status on mount
  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // Compute derived state
  const canUseBiometrics = isSupported && isPlatformAvailable;

  return {
    // State
    isSupported,
    isPlatformAvailable,
    isEnrolled,
    canUseBiometrics,
    isLoading,
    isRegistering,
    isAuthenticating,
    error,
    biometricType,

    // Actions
    checkStatus,
    register,
    authenticate,
    unenroll,
    clearError,
  };
};

export default useBiometricAuth;
